import type { DragEvent, ReactNode } from "react";
import { useState } from "react";
import {
  agentHandlersForBlock,
  blockAuthorLabel,
  filterCollapsedNestedRows,
  projectThreadFeed,
  resolvePresenceLabel,
  type BlockAuthorContext,
  type WaveBlock,
  type WaveFeedRow,
  type WhaleHandlerIdentity,
  type WaveParticipantRecord,
  type WavePresenceRecord,
  type WaveThreadView,
  type WaveTimelineOrder,
} from "@vida/wave-core";
import { WaveAuthorAvatar } from "../presence/WaveAuthorAvatar";
import { WaveAgentAvatarStack } from "../presence/WaveAgentAvatarStack";
import { WHALE_DEFAULT_ICON } from "../../services/waveWhale";
import { WaveIdentityHoverTooltip } from "../presence/WaveIdentityHoverTooltip";
import {
  WAVE_FEED_NODE_SIZE,
  blockKindColor,
  blockKindGlyph,
  formatBlockMetadataLabel,
  formatTimelineGutterTime,
  relationGlyph,
  relationLabel,
  type WaveFeedDensity,
  waveFeedRowSpacing,
} from "./waveFeedTokens";

export type FeedAuthorInfo = {
  sub: string;
  label: string;
  email: string | null;
};

type Props = {
  blocks: WaveBlock[];
  view?: WaveThreadView;
  timelineOrder?: WaveTimelineOrder;
  density?: WaveFeedDensity;
  participants: WaveParticipantRecord[];
  remoteEditorsForBlock: (block: WaveBlock) => WavePresenceRecord[];
  authorForBlock: (block: WaveBlock) => FeedAuthorInfo | null;
  onInsertAfter?: (afterBlockId: string) => void;
  showInsertAffordance?: boolean;
  enableReorder?: boolean;
  onReorderRequest?: (
    sourceBlockId: string,
    targetBlockId: string,
    place: "before" | "after",
  ) => void;
  /** Top-level block ids whose nested children are hidden from the feed. */
  hiddenParentIds?: ReadonlySet<string>;
  children: (row: WaveFeedRow, index: number) => ReactNode;
};

const REORDER_MIME = "application/x-wave-block-reorder";

function sameDay(a: string | undefined, b: string | undefined): boolean {
  if (!a || !b) return false;
  const da = new Date(a);
  const db = new Date(b);
  if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) return false;
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

function FeedDateSeparator({ iso }: { iso: string }) {
  const date = new Date(iso);
  const label = Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
  return (
    <div className="wave-feed-date-separator" role="separator">
      <span className="wave-feed-date-separator-line" aria-hidden />
      <span className="wave-feed-date-separator-label">{label}</span>
    </div>
  );
}

function InsertAffordance({
  afterBlockId,
  onInsertAfter,
}: {
  afterBlockId: string;
  onInsertAfter: (afterBlockId: string) => void;
}) {
  return (
    <div className="wave-feed-insert-row">
      <button
        type="button"
        className="wave-feed-insert-btn"
        aria-label="Inserisci widget qui"
        onClick={() => onInsertAfter(afterBlockId)}
      >
        +
      </button>
    </div>
  );
}

function FeedRow({
  row,
  author,
  participants,
  view,
  isFirst,
  isLast,
  density,
  remoteEditors,
  enableReorder,
  onReorderRequest,
  dragOverState,
  onDragOverChange,
  children,
}: {
  row: WaveFeedRow;
  author: FeedAuthorInfo | null;
  participants: WaveParticipantRecord[];
  view: WaveThreadView;
  isFirst: boolean;
  isLast: boolean;
  density: WaveFeedDensity;
  remoteEditors: WavePresenceRecord[];
  enableReorder?: boolean;
  onReorderRequest?: (
    sourceBlockId: string,
    targetBlockId: string,
    place: "before" | "after",
  ) => void;
  dragOverState: { targetId: string; place: "before" | "after" } | null;
  onDragOverChange: (state: { targetId: string; place: "before" | "after" } | null) => void;
  children: ReactNode;
}) {
  const block = row.block;
  const blockDate = block.createdAt ?? block.updatedAt;
  const nodeColor = blockKindColor(block.kind);
  const remoteEditor = remoteEditors[0];
  const isChild = row.depth === 1;
  const isAgentWidget =
    block.payload.relation === "agent_task" ||
    block.payload.relation === "agent_response";
  const agentAuthorSub = block.payload.authorSub?.trim() || author?.sub || "";
  const agentDisplayName =
    block.payload.authorName?.trim() || author?.label || "Agente";
  const agentHandlers: WhaleHandlerIdentity[] = isAgentWidget
    ? agentHandlersForBlock(block, {
        agentId: agentAuthorSub.startsWith("agent:")
          ? agentAuthorSub.slice("agent:".length)
          : "whale",
        agentName: agentDisplayName,
        agentIcon: block.payload.agentIcon?.trim() || WHALE_DEFAULT_ICON,
        agentIconS3Key: block.payload.agentIconS3Key?.trim() || null,
      })
    : [];
  // Nome riga agente = nome/i dell'agente o degli agenti che hanno svolto l'attività
  // (stesso stack mostrato dagli avatar), non solo l'ultimo authorName.
  const agentHandlersLabel =
    agentHandlers.map((h) => h.agentName).join(", ") || agentDisplayName;
  const showAuthorAvatar =
    author != null || (isAgentWidget && agentHandlers.length > 0);
  const metadataLabel = formatBlockMetadataLabel(block.createdAt, block.updatedAt);
  const canDrag =
    enableReorder &&
    view === "story" &&
    !isChild &&
    !block.id.startsWith("wave-img-group-") &&
    Boolean(onReorderRequest);
  const isDropTarget =
    dragOverState?.targetId === block.id && canDrag;
  const dropPlace = isDropTarget ? dragOverState?.place : null;

  const handleDragStart = (e: DragEvent) => {
    if (!canDrag) return;
    e.dataTransfer.setData(REORDER_MIME, block.id);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: DragEvent) => {
    if (!canDrag || !onReorderRequest) return;
    if (!e.dataTransfer.types.includes(REORDER_MIME)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const place: "before" | "after" = e.clientY < rect.top + rect.height / 2 ? "before" : "after";
    onDragOverChange({ targetId: block.id, place });
  };

  const handleDragLeave = (e: DragEvent) => {
    if (!canDrag) return;
    const related = e.relatedTarget as Node | null;
    if (related && (e.currentTarget as HTMLElement).contains(related)) return;
    if (dragOverState?.targetId === block.id) {
      onDragOverChange(null);
    }
  };

  const handleDrop = (e: DragEvent) => {
    if (!canDrag || !onReorderRequest) return;
    e.preventDefault();
    const sourceId = e.dataTransfer.getData(REORDER_MIME);
    onDragOverChange(null);
    if (!sourceId || sourceId === block.id) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const place: "before" | "after" = e.clientY < rect.top + rect.height / 2 ? "before" : "after";
    onReorderRequest(sourceId, block.id, place);
  };

  return (
    <div
      className={`wave-feed-grid wave-feed-row${isChild ? " wave-feed-row--child" : ""}${isAgentWidget ? " wave-feed-row--agent" : ""}${canDrag ? " wave-feed-row--draggable" : ""}${isDropTarget ? ` wave-feed-row--drop-${dropPlace}` : ""}`}
      style={{
        marginBottom: `${waveFeedRowSpacing(density)}px`,
        ...(isChild ? { paddingLeft: "24px" } : undefined),
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div
        className={`wave-feed-grid-gutter wave-feed-gutter${view === "story" ? " wave-feed-gutter--story" : ""}`}
      >
        <WaveIdentityHoverTooltip
          dateIso={blockDate ?? null}
          email={author?.email ?? null}
          extraLabel={metadataLabel ?? undefined}
        >
          <div className="wave-feed-gutter-inner">
            {view === "timeline" ? (
              <time className="wave-feed-gutter-time">{formatTimelineGutterTime(blockDate)}</time>
            ) : (
              // Niente glyph relazione per i widget agente: l'avatar + nome dell'agente
              // identifica già la riga (evita la doppia icona 🐋 sopra l'avatar).
              !isAgentWidget &&
              row.relation && (
                <span className="wave-feed-gutter-relation" title={relationLabel(row.relation)}>
                  {relationGlyph(row.relation)}
                </span>
              )
            )}
            {showAuthorAvatar && (
              <>
                {isAgentWidget ? (
                  <WaveAgentAvatarStack handlers={agentHandlers} size={32} />
                ) : (
                  <WaveAuthorAvatar
                    authorSub={author!.sub}
                    authorName={author?.email ?? author?.label}
                    participants={participants}
                    size={32}
                  />
                )}
                {isAgentWidget && (
                  <span className="wave-feed-gutter-name" title={agentHandlersLabel}>
                    {agentHandlersLabel}
                  </span>
                )}
              </>
            )}
          </div>
        </WaveIdentityHoverTooltip>
      </div>

      <div className="wave-feed-grid-main wave-feed-card">
        {canDrag && (
          <button
            type="button"
            className="wave-feed-drag-handle"
            draggable
            aria-label="Trascina per riordinare"
            onDragStart={handleDragStart}
            onDragEnd={() => onDragOverChange(null)}
          >
            ⠿
          </button>
        )}
        {children}
      </div>

      <div
        className={`wave-feed-grid-rail wave-feed-rail${isChild ? " wave-feed-rail--child" : ""}`}
      >
        {isChild && <span className="wave-feed-rail-connector" aria-hidden />}
        {!isFirst && <span className="wave-feed-rail-line wave-feed-rail-line--top" />}
        <span
          className="wave-feed-node"
          style={{
            width: WAVE_FEED_NODE_SIZE,
            height: WAVE_FEED_NODE_SIZE,
            color: nodeColor,
            background: `color-mix(in srgb, ${nodeColor} 15%, transparent)`,
          }}
          title={block.kind}
        >
          {blockKindGlyph(block.kind)}
          {remoteEditor && (
            <span className="wave-feed-node-editor">
              <WaveAuthorAvatar
                authorSub={remoteEditor.sub}
                authorName={resolvePresenceLabel(remoteEditor, participants)}
                participants={participants}
                size={14}
              />
            </span>
          )}
        </span>
        {!isLast && <span className="wave-feed-rail-line wave-feed-rail-line--bottom" />}
      </div>
    </div>
  );
}

export function resolveFeedAuthor(
  block: WaveBlock,
  participants: WaveParticipantRecord[],
  context?: BlockAuthorContext,
): FeedAuthorInfo | null {
  return blockAuthorLabel(
    block.payload.authorSub,
    block.payload.authorName,
    participants,
    context,
  );
}

export function WaveTimelineFeed({
  blocks,
  view = "timeline",
  timelineOrder = "asc",
  density = "comfortable",
  participants,
  remoteEditorsForBlock,
  authorForBlock,
  onInsertAfter,
  showInsertAffordance = false,
  enableReorder = false,
  onReorderRequest,
  hiddenParentIds,
  children,
}: Props) {
  const rows = filterCollapsedNestedRows(
    projectThreadFeed(blocks, view, timelineOrder),
    hiddenParentIds,
  );
  const [dragOverState, setDragOverState] = useState<{
    targetId: string;
    place: "before" | "after";
  } | null>(null);

  return (
    <div className="wave-timeline-feed">
      {showInsertAffordance && onInsertAfter && rows.length === 0 && (
        <InsertAffordance afterBlockId="" onInsertAfter={onInsertAfter} />
      )}
      {rows.map((row, index) => {
        const blockDate = row.block.createdAt ?? row.block.updatedAt;
        const prevDate =
          index > 0
            ? rows[index - 1]?.block.createdAt ?? rows[index - 1]?.block.updatedAt
            : undefined;
        const showDate =
          view === "timeline" &&
          index > 0 &&
          !sameDay(blockDate, prevDate);
        return (
          <div
            key={row.block.id}
            data-block-id={row.coalescedSourceBlockIds?.[0] ?? row.block.id}
          >
            {showDate && blockDate && <FeedDateSeparator iso={blockDate} />}
            <FeedRow
              row={row}
              author={authorForBlock(row.block)}
              participants={participants}
              view={view}
              isFirst={index === 0}
              isLast={index === rows.length - 1}
              density={density}
              remoteEditors={remoteEditorsForBlock(row.block)}
              enableReorder={enableReorder}
              onReorderRequest={onReorderRequest}
              dragOverState={dragOverState}
              onDragOverChange={setDragOverState}
            >
              {children(row, index)}
            </FeedRow>
            {showInsertAffordance && onInsertAfter && (
              <InsertAffordance
                afterBlockId={row.coalescedSourceBlockIds?.at(-1) ?? row.block.id}
                onInsertAfter={onInsertAfter}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
